/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState, useContext, createContext, useMemo, useRef } from 'react';
import Map from 'ol/Map';
import { Feature, Geolocation } from 'ol';
import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { defaults as defaultControls, ScaleLine } from 'ol/control';
import { Style, Circle, Fill, Stroke } from 'ol/style';
import { mapView, basemaps, layers } from '@/geospatial';
import { staticPolygonsLayer } from '@/lib/geospatial';
import 'ol/ol.css';
import { Geometry, Point } from 'ol/geom';
import { METERS_PER_UNIT, transform } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { PolygonInfo, MouseCoordinate } from '@/lib/types';
import { storageManager } from '@/lib/utils/storage-manager';

type MapContextType = {
  map: Map | null;
  layerGroup: LayerGroup | null;
  baseLayerGroup: LayerGroup | null;
  mouseCoordinate: MouseCoordinate | null;
  scaleString: string | null;
  features: PolygonInfo[];
  setFeatures: (features: PolygonInfo[]) => void;
  clickedCoordinate: number[] | null;
  setClickedCoordinate: (coord: number[] | null) => void;
  geolocationCoordinates: number[];
  setGeolocationCoordinates: (coords: number[]) => void;
  legendVisible: boolean;
  setLegendVisible: (visible: boolean) => void;
  currentTheme: string;
  setCurrentTheme: (theme: string) => void;
  mapResolution: number;
  isMapLoading: boolean;
};

// Create a context for the map instance
const MapContext = createContext<MapContextType>({
  map: null,
  layerGroup: null,
  baseLayerGroup: null,
  mouseCoordinate: null,
  scaleString: null,
  features: [],
  setFeatures: () => {},
  clickedCoordinate: null,
  setClickedCoordinate: () => {},
  geolocationCoordinates: [],
  setGeolocationCoordinates: () => {},
  legendVisible: true,
  setLegendVisible: () => {},
  currentTheme: '',
  setCurrentTheme: () => {},
  mapResolution: 0,
  isMapLoading: false,
});

export const useMap = () => useContext(MapContext);

export const MapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mapInstance = useRef<Map | null>(null);
  const [clickedCoordinate, setClickedCoordinate] = useState<number[] | null>(null);
  const [geolocationCoordinates, setGeolocationCoordinates] = useState<number[]>([]);
  const [features, setFeatures] = useState<PolygonInfo[]>([]);
  const [mouseCoordinate, setMouseCoordinate] = useState<MouseCoordinate | null>(null);
  const [scaleString, setScaleString] = useState<string | null>(null);
  const [olMap, setOlMap] = useState<Map | null>(null);
  const [legendVisible, setLegendVisible] = useState<boolean>(true);
  const [currentTheme, setCurrentTheme] = useState<string>(() => storageManager.loadTheme());
  const [isMapLoading, setIsMapLoading] = useState<boolean>(false);
  const [mapResolution, setMapResolution] = useState<number>(0);

  // Group layer
  const layerGroup = useMemo(
    () =>
      new LayerGroup({
        layers: layers.map((l) => l.layer),
      }),
    [],
  );

  // Base layer group
  const baseLayerGroup = useMemo(
    () =>
      new LayerGroup({
        layers: basemaps,
      }),
    [],
  );

  useEffect(() => {
    const map = new Map({
      view: mapView,
      controls: defaultControls().extend([new ScaleLine()]),
      layers: [baseLayerGroup, layerGroup],
    });

    const numberWithCommas = (x: number) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const updateResolution = () => {
      // Update resolution based on current zoom level
      const resolution = map.getView().getResolution() || 0;
      const units = map.getView().getProjection().getUnits() || 'm';
      const dpi = 25.4 / 0.28;
      const mpu = METERS_PER_UNIT[units as keyof typeof METERS_PER_UNIT];
      const scale = resolution * mpu * 39.37 * dpi;
      setScaleString(numberWithCommas(Math.floor(scale)));
    };

    const handlePointerMove = (event: any) => {
      const standardCoordinate = transform(event.coordinate, 'EPSG:3857', 'EPSG:4326');
      if (standardCoordinate) {
        setMouseCoordinate({
          lat: parseFloat(standardCoordinate[0].toFixed(4)),
          lng: parseFloat(standardCoordinate[1].toFixed(4)),
        });
      }
    };

    const handleMoveEnd = () => {
      updateResolution();
    };

    const handleMapDrag = () => {
      // Handle map drag
    };

    // Add position and click location features
    const positionFeature = new Feature();
    const clickLocationFeature = new Feature();

    positionFeature.setStyle(
      new Style({
        image: new Circle({
          radius: 6,
          fill: new Fill({ color: '#3399CC' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
      }),
    );

    clickLocationFeature.setStyle(
      new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
      }),
    );

    new VectorLayer({
      map: map,
      source: new VectorSource({
        features: [positionFeature, clickLocationFeature],
      }),
    });

    // Add other layers
    layers.forEach((layer) => {
      if (layer.layer) {
        map.addLayer(layer.layer);
      }
    });

    // Set up tile loading event listeners to track map loading state
    let loadingTiles = 0;
    const tileLoadStart = () => {
      loadingTiles++;
      setIsMapLoading(true);
    };
    
    const tileLoadEnd = () => {
      loadingTiles--;
      if (loadingTiles <= 0) {
        loadingTiles = 0;
        setIsMapLoading(false);
      }
    };

    // Add tile loading listeners to all WMS layers
    const wmsLayers = map
      .getLayers()
      .getArray()
      .filter(
        (layer): layer is TileLayer<TileWMS> =>
          layer instanceof TileLayer && layer.getSource() instanceof TileWMS,
      );

    wmsLayers.forEach((layer) => {
      const source = layer.getSource();
      if (source instanceof TileWMS) {
        source.on('tileloadstart', tileLoadStart);
        source.on('tileloadend', tileLoadEnd);
        source.on('tileloaderror', tileLoadEnd);
      }
    });

    // Set up event listeners
    map.on('click', async (event) => {
      const coordinate = event.coordinate;
      const clickedFeatures: Feature<Geometry>[] = [];

      try {
        // Get feature information from WMS layers
        const view = map.getView();
        const viewResolution = view.getResolution();
        const viewProjection = view.getProjection();

        if (!viewResolution) {
          console.warn('View resolution is undefined');
          return;
        }

        setMapResolution(viewResolution);

        // Center the map on the clicked location to avoid overlap with info drawer
        view.animate({ center: coordinate, duration: 250 });

        // Get all WMS layers
        const wmsLayers = map
          .getLayers()
          .getArray()
          .filter(
            (layer): layer is TileLayer<TileWMS> =>
              layer instanceof TileLayer && layer.getSource() instanceof TileWMS,
          );

        // Process WMS features (filtered layers currently on the map)
        const wmsFeaturePromises = wmsLayers.map(async (layer) => {
          const source = layer.getSource();
          if (!source) return null;

          const url = source.getFeatureInfoUrl(coordinate, viewResolution, viewProjection, {
            INFO_FORMAT: 'application/json',
          });

          if (!url) return null;

          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data.features || [];
          } catch (error) {
            console.error('Error fetching WMS feature info:', error);
            return null;
          }
        });

        // Also query an unfiltered polygons source for GetFeatureInfo so clicks work even when CQL filters hide features
        let staticPolygonFeatures: any[] = [];
        try {
          const staticSource = staticPolygonsLayer.getSource() as TileWMS | null;
          if (staticSource) {
            const staticUrl = staticSource.getFeatureInfoUrl(
              coordinate,
              viewResolution,
              viewProjection,
              { INFO_FORMAT: 'application/json' },
            );
            if (staticUrl) {
              const staticResp = await fetch(staticUrl);
              if (staticResp.ok) {
                const staticData = await staticResp.json();
                staticPolygonFeatures = staticData.features || [];
              }
            }
          }
        } catch (error) {
          console.error('Error fetching static polygon feature info:', error);
        }

        // Wait for all filtered WMS feature requests to complete
        const wmsFeatures = await Promise.all(wmsFeaturePromises);
        const validWmsFeatures = wmsFeatures
          .filter((features): features is any[] => features !== null)
          .flat();

        // Handle vector features
        map.forEachFeatureAtPixel(event.pixel, (feature) => {
          clickedFeatures.push(feature as Feature<Geometry>);
        });

        // Combine unfiltered polygons first, then filtered WMS and vector features
        const allFeatures = [...staticPolygonFeatures, ...validWmsFeatures, ...clickedFeatures];
        // Update state
        setFeatures(allFeatures);

        clickLocationFeature.setGeometry(new Point(coordinate));

        const standardCoordinate = transform(coordinate, 'EPSG:3857', 'EPSG:4326');
        setClickedCoordinate(standardCoordinate);
      } catch (error) {
        console.error('Error processing map click:', error);
        // Clear features and coordinate on error
        setFeatures([]);
        setClickedCoordinate(null);
      }
    });

    map.on('pointermove', handlePointerMove);
    map.on('moveend', handleMoveEnd);
    map.on('movestart', handleMapDrag);

    // Listen for zoom changes specifically
    const view = map.getView();
    const handleZoomChange = () => {
      const newZoom = view.getZoom();
      if (newZoom !== undefined && newZoom !== storageManager.loadZoomLevel()) {
        storageManager.saveZoomLevel(Math.floor(newZoom));
      }
    };
    view.on('change:resolution', handleZoomChange);

    // Set up geolocation
    const geolocation = new Geolocation({
      tracking: true,
      projection: map.getView().getProjection(),
    });

    geolocation.on('change:position', () => {
      const coordinates = geolocation.getPosition();
      if (coordinates) {
        setGeolocationCoordinates(coordinates);
        positionFeature.setGeometry(new Point(coordinates));
      }
    });

    mapInstance.current = map;
    setOlMap(map);

    return () => {
      try {
        // Clean up tile loading listeners
        wmsLayers.forEach((layer) => {
          const source = layer.getSource();
          if (source instanceof TileWMS) {
            source.un('tileloadstart', tileLoadStart);
            source.un('tileloadend', tileLoadEnd);
            source.un('tileloaderror', tileLoadEnd);
          }
        });
        
        // Clean up zoom listener
        const view = map.getView();
        if (view) {
          view.un('change:resolution', handleZoomChange);
        }
        
        // Safely set target to undefined
        if (map) {
          map.setTarget(undefined);
        }
      } catch (error) {
        console.error('Error during map cleanup:', error);
      }
    };
  }, [baseLayerGroup, layerGroup]);

  // Ensure legend is shown on initial load for non-assoctheme themes
  useEffect(() => {
    if (!isMapLoading && currentTheme && currentTheme !== 'assoctheme') {
      setLegendVisible(true);
    }
  }, [isMapLoading, currentTheme, setLegendVisible]);

  return (
    <MapContext.Provider
      value={{
        map: olMap,
        layerGroup,
        baseLayerGroup,
        mouseCoordinate,
        scaleString,
        features,
        setFeatures,
        clickedCoordinate,
        setClickedCoordinate,
        geolocationCoordinates,
        setGeolocationCoordinates,
        legendVisible,
        setLegendVisible,
        currentTheme,
        setCurrentTheme,
        mapResolution,
        isMapLoading,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};
